import { Dropdown, Nav, Badge } from "react-bootstrap";
import { IoNotificationsOutline } from "react-icons/io5";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ApiPath from "../util/ApiPath";
import ApiUtil from "../util/ApiUtil";
import SessionUtil from "../util/SessionUtil";
import ReactLoading from "react-loading";
import { useNavigate } from "react-router-dom";
import echo from "../echoConfig"; // Import Echo config

function Notifications() {
  const { t } = useTranslation();
  const lang = SessionUtil.getLang();
  const [notifyCount, setNotifyCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const isLoggedIn = SessionUtil.isLoggedIn();
  const navigate = useNavigate();
  const [recordCount, setRecordCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);

  const user = SessionUtil.getUser();
  const userId = user?.Id;

  useEffect(() => {
    if (!userId) return; // Don't connect if user not logged in

    const channel = echo.private(`notifications.${userId}`);
    channel.listen(".ReceiveNotifications", (message) => {
      setMessages((prev) => [message, ...prev]);
      setNotifyCount((prev) => prev + 1);
      setRecordCount((prev) => prev + 1);
    });

    return () => {
      echo.leave(`private-notifications.${userId}`);
    };
  }, [userId]);

  const getApi = useCallback(async () => {
    setLoading(true);
    const params = {
      PageNumber: currentPage,
      PageSize: 5,
      OrderBy: "Id Desc",
      UserNotificationsView: {
        Code: lang === "en" ? "EN" : "AR",
        LanguageId: lang === "en" ? 2 : 1,
      },
    };
    try {
      const res = await ApiUtil.postApi(
        ApiPath.GET_ALL_NOTIFICATIONS,
        params,
        "POST"
      );
      if (res.IsSuccessfull) {
        setRecordCount(res.Result.RecordCount);
        const newNotifications = res.Result.Notifications;
        setMessages((prev) => [...prev, ...newNotifications]);
        setNotifyCount(res.Result.NotificationsNotReadCount);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, lang]);

  useEffect(() => {
    if (isLoggedIn) {
      getApi();
    }
  }, [getApi, isLoggedIn]);

  const handleClick = useCallback(
    async (id, itemId, status, entityName, storeId) => {
      const params = { Id: id };
      try {
        const res = await ApiUtil.postApi(
          ApiPath.MARK_GET_ALL_NOTIFICATIONS_AS_READ,
          params,
          "PUT"
        );
        if (res.IsSuccessfull) {
          setMessages((prev) =>
            prev.map((item) =>
              item.Id === id ? { ...item, ReadStatus: true } : item
            )
          );

          !status && setNotifyCount((prev) => prev - 1);
          if (entityName === "ITEM") {
            if (id || itemId || storeId) {
              navigate(`/stores-items/new/${storeId}`, {
                state: { id: storeId, type: "new" },
              });
            }
          } else if (entityName === "CONTACTUS") {
            navigate(`/contact-us`);
          } else if (entityName === "DRIVER") {
            navigate(`/driver-registration-request`);
          } else if (entityName === "ORDERS") {
            navigate(`/operation`);
          }
        }
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    },
    [navigate]
  );

  const handleShowMore = () => {
    setCurrentPage((prev) => prev + 1);
  };

  return (
    <Dropdown
      className="filter border-0"
      align={{ xs: "start" }}
      show={showDropdown}
      onToggle={() => setShowDropdown(!showDropdown)}
    >
      <Dropdown.Toggle className="border-0">
        <Nav.Link className="position-relative">
          <IoNotificationsOutline size={"20"} className="icon-color-grey" />
          <Badge
            className={`position-absolute badge-custome ${
              notifyCount > 9 && "px-1"
            }`}
            pill
            bg="danger"
          >
            {notifyCount > 9 ? "+9" : notifyCount}
          </Badge>
        </Nav.Link>
      </Dropdown.Toggle>

      <Dropdown.Menu
        className={`"border-0 shadow-lg p-2 max-h-300 max-w-250 position-absolute" `}
      >
        {messages.length > 0 ? (
          messages.map((msg, index) => {
            let jsonString,
              jsonObject,
              updatedString = msg?.Message,
              id,
              storeId,
              OrderId,
              DriverId;
            jsonString = msg?.Variables;
            jsonObject = jsonString;

            id = jsonObject ? jsonObject?.Id : "";
            OrderId = jsonObject ? jsonObject?.OrderId : "";
            DriverId = jsonObject ? jsonObject?.DriverId : "";
            storeId = jsonObject ? jsonObject?.StoreId : "";
            updatedString = msg?.Message.replace(/@Id@/g, id)
              .replace(/@OrderId@/g, OrderId)
              .replace(/@DriverId@/g, DriverId)
              .replace(/@StoreId@/g, storeId);
            return (
              <div key={index}>
                <Dropdown.Item
                  onClick={() => {
                    handleClick(
                      msg.Id,
                      id,
                      msg.ReadStatus,
                      msg.EntityName,
                      storeId
                    );
                  }}
                  className={` ${!msg.ReadStatus && "bg-notify-not-read"}`}
                >
                  <div className="d-flex gap-2 font-13 align-items-start">
                    <div className="position-relative">
                      <div className="get-initials bg-grey dim-30">
                        <IoNotificationsOutline size={"20"} color="#555" />
                      </div>
                      {!msg.ReadStatus && (
                        <span className="circle-state bg-secondary"></span>
                      )}
                    </div>
                    <div>
                      <p className="mb-0 fw-bold">{msg.Title}</p>
                      <p className="mb-0 text-wrap overflow-hidden">
                        {updatedString}
                      </p>
                    </div>
                  </div>
                </Dropdown.Item>
                {index < messages.length - 1 && <Dropdown.Divider />}
              </div>
            );
          })
        ) : (
          <Dropdown.Item className="text-center font-13 flex-gap-2 align-items-center py-2">
            <IoNotificationsOutline size={"20"} color="#555" />
            {t("There are no notifications to display")}
          </Dropdown.Item>
        )}
        {loading && (
          <Dropdown.Item className="d-flex justify-content-center">
            <ReactLoading
              type={"spinningBubbles"}
              color={"#c8c8c8"}
              height={"20px"}
              width={"20px"}
            />
          </Dropdown.Item>
        )}
        {messages.length < recordCount && (
          <Dropdown.Item
            as={"button"}
            onClick={handleShowMore}
            className="font-13 fw-semibold text-center py-2 bg-light text-main-color"
          >
            {t("Show More")}
          </Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default Notifications;
